import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { Fragment, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import useGet from "../../../services/hooks/useGet";
import { apiEndpoints } from "../../../shared/constants/api-constant";
import {
  ManageSelectValue,
  UserRoleValues,
} from "../../../shared/constants/enums";
import { Heading, SearchBar } from "../../../shared/ReusableComponents";
import { GridTable } from "../../../shared/ReusableComponents/GridTable";
import IconButton from "../../../shared/ReusableComponents/IconButton";
import { hideLoader, showLoader } from "../../../store/currentPreferencesSlice";
import GridEditPopup from "./GridEditPopup";

function GridandSearch({
  label,
  buttonValue,
  selectedValue,
  onAssignRoleBtn,
}: any) {
  const mainColumns: GridColDef[] = [
    {
      headerName: "Student/Tutor Code",
      field: "code",
    },
    {
      headerName: "First Name",
      field: "userFirstName",
    },
    {
      headerName: "Last Name",
      field: "userLastName",
    },
    {
      headerName: "Login ID",
      field: "userName",
    },
    {
      headerName: "Email ID",
      field: "email",
    },
    {
      field: "roleId",
      headerName: "Role Name",
      renderCell: (params: any) => {
        const roleId = params.row?.roleId;
        const roleName = UserRoleValues[roleId as keyof typeof UserRoleValues];
        return roleName;
      },
    },
    {
      field: "cohortName",
      headerName: "Cohort Name",
    },
    {
      field: "statusId",
      headerName: "Status",
      renderCell: (params: any) => {
        return params.row?.statusId === 1 ? "Active" : "Inactive";
      },
    },
    {
      field: "action",
      headerName: "Action",
      renderCell: (params: any) => {
        return (
          <Box
            sx={{
              display: "flex",
              cursor: "pointer",
              alignItems: "center",
              marginTop: "10px",
              justifyContent: "center",
            }}
            onClick={() => {
              setSelectedRow(params.row);
              setIsOpen(true);
            }}
          >
            <span className="material-symbols-outlined">edit</span>
          </Box>
        );
      },
    },
  ];

  const [searchText, setSearchText] = useState("");
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [columns, setColumns] = useState<any>([]);
  const [gridData, setGridData] = useState<any[]>([]);
  const [filterGridData, setFilterGridData] = useState<any[]>([]);
  const [cohortList, setCohortList] = useState<any[]>([]);
  const [prevRoles, setPrevRoles] = useState("");
  const { loading, data, getService } = useGet();
  const { data: CohortData, getService: cohortService } = useGet();
  const dispatch = useDispatch();
  const handleSearch = (searchText: string) => {
    setSearchText(searchText);
  };

  useEffect(() => {
    selectedValue === ManageSelectValue.STAFF && setColumns([]);
    setSearchText("");
    onGetTableData();
  }, [selectedValue]);

  useEffect(() => {
    const filteredData =
      gridData && gridData.length
        ? searchText
          ? gridData.filter((row: any) =>
              row.code?.toLowerCase()?.includes(searchText.toLowerCase())
            )
          : gridData
        : [];
    setFilterGridData(filteredData);
  }, [searchText, gridData]);

  useEffect(() => {
    setGridData([]);
    setColumns(updatedColumns());
    if (data?.length) {
      const updatedData = data.map((item: any) => {
        const cohortName =
          CohortData?.find((cohort: any) => cohort.id === item.cohortId)
            ?.cohortName || "NA";
        return { ...item, cohortName };
      });

      setGridData(updatedData);
      CohortData && setCohortList(CohortData);
    }
  }, [data, CohortData, selectedValue]);

  const updatedColumns = () => {
    return selectedValue === ManageSelectValue.STAFF
      ? mainColumns
          .filter((col: any) => !["cohortName", "action"].includes(col.field))
          .map((col: any) =>
            col.field === "code" ? { ...col, headerName: "Staff Code" } : col
          )
      : mainColumns;
  };

  useEffect(() => {
    loading && dispatch(showLoader());
    !loading && dispatch(hideLoader());
  }, [loading]);

  const onGetTableData = (isSkip = false) => {
    const userRoleValues =
      selectedValue === ManageSelectValue.STAFF
        ? `${UserRoleValues["Super User"]},${UserRoleValues["Subject Coordinator"]}`
        : `${UserRoleValues["Student"]},${UserRoleValues["Tutor"]}`;
    if (!isSkip && userRoleValues === prevRoles) return;
    setPrevRoles(userRoleValues);
    setGridData([]);
    selectedValue !== ManageSelectValue.STAFF &&
      cohortService(`${apiEndpoints.COHORT}`);
    getService(`${apiEndpoints.USERS}?commaSeparatedRoleIds=${userRoleValues}`);
  };

  const closePopup = (value: boolean) => {
    setIsOpen(false);
    setSelectedRow(null);
    value && onGetTableData(true);
  };

  return (
    <Fragment>
      {isOpen && (
        <GridEditPopup
          isOpen={isOpen}
          row={selectedRow}
          type={selectedValue}
          cohortList={cohortList}
          onClose={(value: boolean) => closePopup(value)}
        />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 5px",
        }}
      >
        <Box
          sx={{
            fontSize: "20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Heading text={label} type="h5" isBold={true} />
          <IconButton
            onClick={
              selectedValue === ManageSelectValue.BULK && gridData.length > 0
                ? undefined
                : onAssignRoleBtn
            }
            className="iconButton"
            text={buttonValue}
            icon={
              <span className="material-symbols-outlined pr-5">Add_circle</span>
            }
          />
          {selectedValue === ManageSelectValue.BULK && gridData?.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                color: "var(--color-info-main)",
              }}
            >
              <span className="material-symbols-outlined pr-5">info</span>
              <Heading
                text="Bulk Upload will be available once existing Student and Tutor data is Archived."
                type="body2"
                isBold={true}
              />
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-end",
          }}
        >
          <SearchBar
            value={searchText}
            onSearch={handleSearch}
            className="search-bar-temp-width"
            title={`Search by ${
              selectedValue === ManageSelectValue.STAFF
                ? "Staff Code"
                : "Student/Tutor Code"
            }`}
            placeholder={`Search by ${
              selectedValue === ManageSelectValue.STAFF
                ? "Staff Code"
                : "Student/Tutor Code"
            }`}
          />
        </Box>
      </Box>
      <Box className="gridTable">
        <GridTable
          rows={filterGridData}
          columns={columns}
          pageSizeOptions={[5, 10, 15]}
          initialPageSize={10}
        />
      </Box>
    </Fragment>
  );
}

export default GridandSearch;
